import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, X, ImagePlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { GalleryUploader } from "@/components/GalleryUploader";

const SECTORS = [
  "Agriculture", "Technology", "Retail & Trade", "Food & Hospitality",
  "Health & Wellness", "Education", "Finance & Legal",
  "Construction & Real Estate", "Creative & Media", "Fashion & Beauty",
  "Manufacturing", "Transport & Logistics", "Professional Services", "Other",
];

const inputCls =
  "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55";

export const EditBusinessModal = ({ business, onClose, onSaved }) => {
  const [form, setForm] = useState({
    owner_name: business.owner_name || "",
    business_name: business.business_name || "",
    sector: business.sector || "",
    phone: business.phone || "",
    email: business.email || "",
    description: business.description || "",
    location: business.location || "",
    website: business.website || "",
    social: business.social || "",
    offerings: business.offerings || "",
    needs: business.needs || "",
  });
  const [logo, setLogo] = useState(
    business.logo_path
      ? { path: business.logo_path, preview: `${process.env.REACT_APP_BACKEND_URL}/api/files/${business.logo_path}` }
      : null
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState(
    (business.gallery || []).map((g) => {
      const path = typeof g === "string" ? g : g.path;
      return {
        path,
        preview: `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`,
        caption: typeof g === "string" ? "" : g.caption || "",
      };
    })
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads", fd);
      setLogo({ path: res.data.path, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not upload the image."));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/businesses/${business.id}`, { ...form, logo_path: logo?.path || "", gallery: gallery.map((g) => ({ path: g.path, caption: g.caption || "" })) });
      toast.success(`${res.data.business_name} updated.`);
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not save changes."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-charcoal/50 px-4 py-10 backdrop-blur-sm"
      onClick={onClose}
      data-lenis-prevent
      data-testid="edit-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl border border-stone_warm bg-white p-8 shadow-2xl sm:p-10"
        data-testid="edit-business-modal"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Edit listing</p>
            <h2 className="font-serif text-3xl font-light tracking-tight text-charcoal">{business.business_name}</h2>
          </div>
          <button onClick={onClose} className="text-charcoal/40 transition-colors hover:text-charcoal" data-testid="edit-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="mt-8 space-y-6" data-testid="edit-business-form">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="edit-owner" className={labelCls}>Owner name</label>
              <input id="edit-owner" required value={form.owner_name} onChange={set("owner_name")} className={inputCls} data-testid="edit-owner-name-input" />
            </div>
            <div>
              <label htmlFor="edit-business" className={labelCls}>Business name</label>
              <input id="edit-business" required value={form.business_name} onChange={set("business_name")} className={inputCls} data-testid="edit-business-name-input" />
            </div>
            <div>
              <label htmlFor="edit-sector" className={labelCls}>Sector</label>
              <select id="edit-sector" required value={form.sector} onChange={set("sector")} className={inputCls} data-testid="edit-sector-select">
                <option value="" disabled>Select a sector</option>
                {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="edit-phone" className={labelCls}>Phone</label>
              <input id="edit-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="edit-phone-input" />
            </div>
            <div>
              <label htmlFor="edit-email" className={labelCls}>Email</label>
              <input id="edit-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="edit-email-input" />
            </div>
            <div>
              <label htmlFor="edit-location" className={labelCls}>Location</label>
              <input id="edit-location" required value={form.location} onChange={set("location")} className={inputCls} data-testid="edit-location-input" />
            </div>
            <div>
              <label htmlFor="edit-website" className={labelCls}>Website</label>
              <input id="edit-website" value={form.website} onChange={set("website")} className={inputCls} data-testid="edit-website-input" />
            </div>
            <div>
              <label htmlFor="edit-social" className={labelCls}>Social handle</label>
              <input id="edit-social" value={form.social} onChange={set("social")} className={inputCls} data-testid="edit-social-input" />
            </div>
          </div>

          <div>
            <label htmlFor="edit-description" className={labelCls}>About the business</label>
            <textarea id="edit-description" required rows={3} value={form.description} onChange={set("description")} className={inputCls} data-testid="edit-description-input" />
          </div>
          <div>
            <label htmlFor="edit-offerings" className={labelCls}>Products & services</label>
            <textarea id="edit-offerings" required rows={3} value={form.offerings} onChange={set("offerings")} className={inputCls} data-testid="edit-offerings-input" />
          </div>
          <div>
            <label htmlFor="edit-needs" className={labelCls}>Needs help with</label>
            <textarea id="edit-needs" rows={2} value={form.needs} onChange={set("needs")} className={inputCls} data-testid="edit-needs-input" />
          </div>

          <div>
            <span className={labelCls}>Logo or photo</span>
            <div className="flex items-center gap-5">
              {logo ? (
                <img src={logo.preview} alt="Logo preview" className="h-16 w-16 rounded-full border border-stone_warm object-cover" data-testid="edit-logo-preview" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-stone_warm bg-[#F3F0EB] text-charcoal/35">
                  <ImagePlus size={18} />
                </span>
              )}
              <label className="cursor-pointer rounded-full border border-charcoal/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark" data-testid="edit-logo-label">
                {uploading ? "Uploading…" : logo ? "Replace image" : "Choose image"}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogo} disabled={uploading} data-testid="edit-logo-input" />
              </label>
            </div>
          </div>

          <GalleryUploader gallery={gallery} onChange={setGallery} testIdPrefix="edit-gallery" />

          {business.edit_token && (
            <div className="rounded-xl border border-gold/40 bg-[#F3F0EB] p-5" data-testid="owner-edit-link-block">
              <p className={labelCls}>Owner edit link</p>
              <p className="mb-3 text-xs leading-relaxed text-charcoal/60">
                Share this private link with the owner so they can update their own listing.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <code className="max-w-full truncate rounded-lg border border-stone_warm bg-white px-3 py-2 font-mono text-[11px] text-charcoal/70" data-testid="owner-edit-link-text">
                  {`${window.location.origin}/edit/${business.edit_token}`}
                </code>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${window.location.origin}/edit/${business.edit_token}`);
                      toast.success("Owner edit link copied.");
                    } catch {
                      toast.error("Could not copy — please copy the link manually.");
                    }
                  }}
                  className="rounded-full border border-charcoal/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
                  data-testid="copy-owner-link-button"
                >
                  Copy link
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-stone_warm pt-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-charcoal/25 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
              data-testid="edit-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="inline-flex items-center gap-2 rounded-full bg-charcoal px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-gold disabled:opacity-60"
              data-testid="edit-save-button"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
